import { Badge } from '@/components/ui/badge';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import {
    FormSection,
    MasterDataForm,
} from '../components/master-data/MasterDataForm';
import {
    FilterConfig,
    MasterDataTable,
    TableColumn,
} from '../components/master-data/MasterDataTable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { PRODUCT_FORMAT_OPTIONS, ProductFormat } from '../types/productFormat';

const productFormatOptions = PRODUCT_FORMAT_OPTIONS.map(opt => ({
  value: opt,
  label: opt
}));

const ProductFormatManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProductFormat, setEditingProductFormat] = useState<ProductFormat | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side pagination, filtering, and searching
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>(
    {}
  );
  const [sortConfig, setSortConfig] = useState<{
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }>({});

  // Fetch Product Formats with server-side filtering and pagination
  const {
    data: productFormatsResponse,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      'productFormats',
      currentPage,
      pageSize,
      searchTerm,
      currentFilters,
      sortConfig,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(
        `/api/master-data/productFormat?${params.toString()}`
      );
      const result = response.data.data || {};
      console.log('ProductFormat API response:', result);

      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1,
        },
      };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const productFormats = productFormatsResponse?.data || [];
  const pagination = productFormatsResponse?.pagination;

  // Create mutation
  const createProductFormatMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/productFormat', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productFormats'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'productFormat'],
      });
      toast({
        title: 'Success',
        description: 'Product Format created successfully',
        variant: 'default',
      });
      setIsFormOpen(false);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to create Product Format';
      const errors = error.response?.data?.error?.details || {};

      setServerErrors(errors);

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Update mutation
  const updateProductFormatMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await axios.put(
        `/api/master-data/productFormat/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productFormats'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'productFormat'],
      });
      toast({
        title: 'Success',
        description: 'Product Format updated successfully',
        variant: 'default',
      });
      setIsFormOpen(false);
      setEditingProductFormat(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to update Product Format';
      const errors = error.response?.data?.error?.details || {};

      setServerErrors(errors);

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Delete mutation (hard delete)
  const deleteProductFormatMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(
        `/api/master-data/productFormat/${id}?soft=false`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productFormats'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'productFormat'],
      });
      toast({
        title: 'Success',
        description: 'Product Format deleted successfully',
        variant: 'default',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description:
          error.response?.data?.error?.message || 'Failed to delete Product Format',
        variant: 'destructive',
      });
    },
  });

  // Get format badge color
  const getFormatBadgeClass = (name: string) => {
    switch (name) {
      case 'Package':
        return 'bg-blue-500 hover:bg-blue-600';
      case 'Void':
        return 'bg-gray-500 hover:bg-gray-600';
      case 'Non Dox':
        return 'bg-purple-500 hover:bg-purple-600';
      case 'Dox':
        return 'bg-indigo-500 hover:bg-indigo-600';
      case 'Others':
        return 'bg-orange-500 hover:bg-orange-600';
      default:
        return '';
    }
  };

  // Table columns configuration
  const columns: TableColumn<ProductFormat>[] = [
    {
      key: 'name',
      label: 'Product Format Name',
      sortable: true,
      render: (_, productFormat) => (
        <Badge
          variant="default"
          className={getFormatBadgeClass(productFormat.name)}
        >
          {productFormat.name}
        </Badge>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (_, productFormat) => (
        <Badge
          variant={productFormat.status === 'Active' ? 'default' : 'secondary'}
          className={
            productFormat.status === 'Active'
              ? 'bg-green-500 hover:bg-green-600'
              : ''
          }
        >
          {productFormat.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      hideOnTablet: true,
      render: (_, productFormat) => (
        <div className='text-sm text-muted-foreground'>
          {new Date(productFormat.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </div>
      ),
    },
  ];

  // Filter configuration
  const enhancedFilterConfig: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
      ],
      placeholder: 'Filter by status',
    },
    {
      key: 'name',
      label: 'Format',
      type: 'select',
      options: [
        { value: 'all', label: 'All Formats' },
        ...productFormatOptions,
      ],
      placeholder: 'Filter by format',
    },
  ];

  // Form sections configuration
  const formSections: FormSection[] = [
    {
      title: 'Product Format Information',
      description: 'Select the product format type.',
      fields: [
        {
          name: 'name',
          label: 'Product Format Name',
          type: 'select',
          required: true,
          placeholder: 'Select product format',
          description: 'Choose from the available product format options',
          options: productFormatOptions,
          validation: z.enum(['Package', 'Void', 'Non Dox', 'Dox', 'Others'], {
            errorMap: () => ({ message: 'Please select a valid product format' })
          }),
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: [
            { value: 'Active', label: 'Active - Available for use' },
            { value: 'Inactive', label: 'Inactive - Not available for use' },
          ],
          description: 'Current status of this product format',
        },
      ],
    },
  ];

  // Event handlers
  const handleCreate = () => {
    setEditingProductFormat(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (productFormat: ProductFormat) => {
    setEditingProductFormat(productFormat);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = async (productFormat: ProductFormat) => {
    if (
      window.confirm(
        `Are you sure you want to permanently delete product format "${productFormat.name}"? This action cannot be undone.`
      )
    ) {
      deleteProductFormatMutation.mutate(productFormat._id);
    }
  };

  const handleSubmit = async (data: any) => {
    if (editingProductFormat) {
      updateProductFormatMutation.mutate({
        id: editingProductFormat._id,
        data,
      });
    } else {
      createProductFormatMutation.mutate(data);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingProductFormat(null);
    setServerErrors({});
  };

  const handleRefresh = () => {
    refetch();
    toast({
      title: 'Refreshed',
      description: 'Product Format data has been refreshed',
      variant: 'default',
    });
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();

      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(
        `/api/master-data/productFormat/export?${params.toString()}`
      );

      if (response.data.success) {
        const productFormats = response.data.data;

        const exportData = productFormats.map((item: ProductFormat) => ({
          'Product Format Name': item.name,
          Status: item.status,
          'Created At': new Date(item.createdAt).toLocaleString(),
          'Updated At': new Date(item.updatedAt).toLocaleString(),
        }));

        const headers = Object.keys(exportData[0]);
        const csvContent = [
          headers.join(','),
          ...exportData.map((row: any) =>
            headers
              .map((header) => {
                const value = row[header];
                if (
                  typeof value === 'string' &&
                  (value.includes(',') || value.includes('"'))
                ) {
                  return `"${value.replace(/"/g, '""')}"`;
                }
                return value;
              })
              .join(',')
          ),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `product-formats-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast({
          title: 'Success',
          description: `Exported ${productFormats.length} Product Formats`,
          variant: 'default',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Export Failed',
        description:
          error.response?.data?.error?.message || 'Failed to export Product Formats',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>
            Product Format Management
          </h1>
          <p className='text-muted-foreground'>
            Manage product format types and their configurations
          </p>
        </div>
      </div>

      <MasterDataTable<ProductFormat>
        title='Product Formats'
        description='Manage and configure product format information'
        data={productFormats}
        columns={columns}
        isLoading={isLoading}
        serverSide={true}
        pagination={pagination}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        currentSearch={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder='Search product formats...'
        currentFilters={currentFilters}
        onFiltersChange={setCurrentFilters}
        filters={enhancedFilterConfig}
        currentSort={sortConfig}
        onSortChange={setSortConfig}
        onRefresh={handleRefresh}
        onExport={handleExport}
        onAdd={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
        allowImportExport={true}
      />

      <MasterDataForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        title={editingProductFormat ? 'Edit Product Format' : 'Create Product Format'}
        sections={formSections}
        initialData={editingProductFormat}
        onSubmit={handleSubmit}
        isLoading={
          createProductFormatMutation.isPending || updateProductFormatMutation.isPending
        }
        mode={editingProductFormat ? 'edit' : 'create'}
        serverErrors={serverErrors}
      />
    </div>
  );
};

export default ProductFormatManagement;


